import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import ReactSelect from "react-select";
import RippleLoader from "../../components/RippleLoader/RippleLoader";
import { DefaultButton, InputWithLabel } from "../../components/micro/input";
import { Eye, EyeSlash, MailBox } from "../../assets/svg";
import { InputError } from "../../components/error/error";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import { getCountryData } from "../../redux/address/slice";
import Breadcrumb from "../../components/common/BreadCrumb";
import { adminGetProfileData, adminProfile } from "../../redux/auth/slice";

const Profile = () => {
  const [countryArray, setCountryArray] = useState([]);
  const [passwordType, setPasswordType] = useState("password");
  const [isEditing, setIsEditing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [profileValues, setProfileValues] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { getProfileData, profileLoading, countryData, countryLoading } =
    useSelector((store) => ({
      getProfileData: store?.authData?.adminGetProfileDataStatus?.data,
      profileLoading: store?.authData?.loading,
      countryData: store?.addressData?.getCountryDataStatus?.data,
      countryLoading: store?.addressData?.getCountryDataStatus?.loading,
    }));

  useEffect(() => {
    setIsLoading(true);
    dispatch(adminGetProfileData({ navigate }));
    dispatch(getCountryData());
    setIsLoading(false);
  }, [dispatch]);

  useEffect(() => {
    if (countryData?.data) {
      let data = countryData?.data?.map((x) => ({ ...x, label: x.phonecode }));
      setCountryArray(data);
    }
  }, [countryData]);

  const countryValueGet = {
    label: getProfileData?.country?.phonecode,
    ...getProfileData?.country,
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      const res = await dispatch(adminProfile(profileValues));

      if (res.type === "adminProfile/fulfilled") {
        dispatch(adminGetProfileData({ navigate }));
      }
    } catch (error) {
      console.error("Error creating staff:", error);
    } finally {
      setIsModalOpen(false);
      setIsEditing(false);
    }
  };

  return (
    <>
      {profileLoading ? (
        <RippleLoader />
      ) : (
        <section>
          <p className="text-xl font-medium">Profile</p>
          <Breadcrumb
            breadCrumbParent={"Home"}
            breadCrumbActive={"My Profile"}
            breadCrumbTitleKey={"/"}
            classes=""
          />
          <div className="flex items-center space-x-5 pt-4 mb-6">
            <h6 className="font-Montserrat font-normal text-[34px] ">
              My Profile
            </h6>
          </div>
          <div>
            {/* User Profile */}
            <Formik
              enableReinitialize={true}
              initialValues={{
                full_name: getProfileData?.full_name ?? "",
                phone: getProfileData?.phone ?? "",
                email: getProfileData?.email ?? "",
                country_id: getProfileData?.country?.phonecode
                  ? countryValueGet
                  : "",
                password: getProfileData?.password ?? "",
              }}
              validationSchema={Yup.object().shape({
                full_name: Yup.string().min(3).required("Please enter name"),
                phone: Yup.string()
                  .required("Please enter mobile")
                  .max(
                    13,
                    "Mobile is too long - should be 10 characters maximum."
                  ),
                country_id: Yup.object().required("Please select country_id"),
                email: Yup.string().min(3).required("Please enter e-mail"),
                password: getProfileData
                  ? Yup.string()
                      .min(
                        8,
                        "Password is too short - should be 8 characters minimum."
                      )
                      .matches(
                        /[a-zA-Z]/,
                        "Password can only contain Latin letters."
                      )
                  : Yup.string()
                      .required("Please enter password")
                      .min(
                        8,
                        "Password is too short - should be 8 characters minimum."
                      )
                      .matches(
                        /[a-zA-Z]/,
                        "Password can only contain Latin letters."
                      ),
              })}
              onSubmit={(values) => {
                if (isEditing) {
                  const updatedValues = {
                    ...values,
                    country_id: values?.country_id?.id ?? null,
                  };
                  setIsModalOpen(true);
                  setProfileValues(updatedValues);
                }
              }}
            >
              {({
                values,
                errors,
                touched,
                handleBlur,
                handleChange,
                handleSubmit,
                setFieldValue,
              }) => (
                <Form onSubmit={handleSubmit}>
                  <div className="w-full bg-bgWhite  shadow drop-shadow-md rounded-lg py-8 px-6 space-y-6 ">
                    {/* Name */}
                    <InputWithLabel
                      className={`${
                        isEditing === "edit" && !isEditing
                          ? "text-textGrey border border-dashed "
                          : "text-black border hover:border-themeBtn"
                      }  ${
                        errors?.full_name && touched?.full_name
                          ? `border border-red`
                          : `border border-borderTheme/80 focus-within:border-themeBtn `
                      }  transition duration-300 ease-in-out py-1`}
                      idFromName="full_name"
                      label="Name"
                      type="text"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values?.full_name}
                      errors={errors?.full_name && touched?.full_name}
                      errorsText={errors?.full_name}
                      disabled={!isEditing}
                    />
                    <div className="flex gap-5">
                      {/* Country */}
                      <div className="max-w-40 w-full">
                        <div
                          className={`${
                            errors?.country_id && touched?.country_id
                              ? ` input-error `
                              : ""
                          } relative bg-white transition duration-300 rounded plus-number-inputs`}
                        >
                          <ReactSelect
                            className={"text-black p-0"}
                            id="country_id"
                            placeholder="Country"
                            options={countryArray}
                            isLoading={countryLoading}
                            getOptionValue={(countryArray) =>
                              countryArray?.phonecode
                            }
                            value={values?.country_id}
                            onChange={(e) => {
                              setFieldValue("country_id", e);
                            }}
                            isDisabled={!isEditing}
                            styles={{
                              control: (base) => ({
                                ...base,
                                border: `1px solid ${
                                  errors?.country_id && touched?.country_id
                                    ? "red"
                                    : "#0000003B"
                                }`,
                                borderRadius: "0.25rem",
                                padding: "3px 3px",
                                boxShadow: "none",
                                "&:hover": {
                                  border: "1px solid #0000003B",
                                },
                              }),
                            }}
                          />
                          <label
                            htmlFor="country_id"
                            className="absolute text-light_grey pointer-events-none text-base bg-white duration-300 transform -translate-y-5 scale-75 top-2 z-10 origin-[0] px-2 peer-focus:px-2 peer-focus peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 left-1"
                          >
                            Country
                          </label>
                        </div>
                        {errors?.country_id && touched?.country_id ? (
                          <InputError errorTitle={errors?.country_id} />
                        ) : null}
                      </div>
                      {/* Mobile */}
                      <div className="w-full">
                        <InputWithLabel
                          className={`${
                            isEditing === "edit" && !isEditing
                              ? "text-textGrey border border-dashed "
                              : "text-black border hover:border-themeBtn"
                          }  ${
                            errors?.phone && touched?.phone
                              ? `border border-red`
                              : `border border-borderTheme/80 focus-within:border-themeBtn `
                          }  transition duration-300 ease-in-out  py-1`}
                          idFromName="phone"
                          label="Mobile Number"
                          type={"number"}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values?.phone}
                          errors={errors?.phone && touched?.phone}
                          errorsText={errors?.phone}
                          disabled={!isEditing}
                          onFocus={(e) =>
                            e.target.addEventListener(
                              "wheel",
                              function (e) {
                                e.preventDefault();
                              },
                              { passive: false }
                            )
                          }
                        />
                      </div>
                    </div>
                    {/* Email */}
                    <div>
                      <div
                        className={`${
                          errors?.email && touched?.email
                            ? `border border-red `
                            : `  `
                        } relative bg-white  transition duration-300 rounded`}
                      >
                        <input
                          type="email"
                          id="email"
                          name="email"
                          className={`block px-2.5 py-3 pl-11 w-full text-sm error-forms ${
                            isEditing === "edit" && !isEditing
                              ? "text-textGrey border border-dashed "
                              : "text-black border hover:border-themeBtn"
                          } bg-transparent rounded border-borderTheme/80 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer`}
                          placeholder={"myemail@gmail.com"}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={!isEditing}
                          value={values?.email}
                        />
                        <MailBox className="absolute top-1/2 left-3 -translate-y-1/2" />
                        <label
                          htmlFor="email"
                          className="absolute top-0 left-1 -translate-y-2.5 text-light_grey pointer-events-none text-base scale-75 px-1 bg-white"
                        >
                          Email
                        </label>
                      </div>
                      {errors?.email && touched?.email ? (
                        <InputError errorTitle={errors?.email} />
                      ) : null}
                    </div>
                    {/* Password */}
                    <div>
                      <div
                        className={`${
                          errors?.email && touched?.email
                            ? ` border border-red `
                            : `  `
                        } relative bg-white  transition duration-300 rounded`}
                      >
                        <input
                          type={passwordType}
                          id="password"
                          name="password"
                          className={`block px-2.5 py-3 pl-11 w-full text-sm error-forms ${
                            isEditing === "edit" && !isEditing
                              ? "text-textGrey border border-dashed "
                              : "text-black border hover:border-themeBtn"
                          } bg-transparent rounded border-borderTheme/80 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer`}
                          placeholder={"Example@123"}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values?.password}
                          disabled={!isEditing}
                        />
                        <p
                          className="absolute top-1/2 left-3 -translate-y-1/2"
                          onClick={() => {
                            if (isEditing) {
                              setPasswordType(
                                passwordType === "password"
                                  ? "text"
                                  : "password"
                              );
                            }
                          }}
                        >
                          {passwordType === "password" ? <EyeSlash /> : <Eye />}
                        </p>
                        <label
                          htmlFor="password"
                          className="absolute top-0 left-1 -translate-y-2.5 text-light_grey pointer-events-none text-base scale-75 px-1 bg-white"
                        >
                          Password
                        </label>
                      </div>
                      {errors?.password && touched?.password ? (
                        <InputError errorTitle={errors?.password} />
                      ) : null}
                    </div>
                  </div>
                  {/* Buttons */}
                  <div className="flex justify-end mt-4">
                    {isEditing ? (
                      <DefaultButton value={"Save changes"} type="submit" />
                    ) : (
                      <>
                        <div
                          className="bg-themeBtn text-white flex items-center justify-center px-6 py-2.5 rounded uppercase font-Proxima cursor-pointer"
                          onClick={handleEditClick}
                        >
                          Edit
                        </div>
                      </>
                    )}
                  </div>
                  {isModalOpen && (
                    <ConfirmationModel
                      confirmText={"Save"}
                      isOpen={isModalOpen}
                      onClose={() => setIsModalOpen(false)}
                      isLoading={isLoading}
                      onConfirm={handleSave}
                    />
                  )}
                </Form>
              )}
            </Formik>
          </div>
        </section>
      )}
    </>
  );
};

export default Profile;
